import React, { useRef, useContext, useState } from 'react';
import {
  Icon,
  Icons,
  Menu,
  MenuItem,
  Typography,
  styled,
} from '@nextiva/next-ui-components';
import { useSelector } from 'react-redux';
import {
  actionTypes,
  LicensesContext,
} from 'containers/pages/BillingLicenses/licensesContext';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  StyledMenuIconButton,
  StyledMenuIcon,
} from 'components/MenuIconButton';
import { PopoverOrigin, LicensesRowMenuProps } from './LicensesRowMenuTypes';

const StyledShoppingCartIcon = styled(Icon)`
  fill: ${({ theme }) => theme.palette.blueGrey.A400};
  transform: scale(1.13);
`;
const StyledMenuItemContainer = styled('div')`
  display: flex;
  align-items: center;
  margin: 4px 8px 4px 11px;
`;
const StyledMenuItemText = styled(Typography)`
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  margin-left: 13px;
`;

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 165,
};

interface GlobalPolicyDecisionRequestState {
  purchaseLicenseFlowGrantStatus: boolean;
}

interface AccountAttributes {
  inAppPurchaseEnabled: boolean | string;
  freemium: boolean | string;
}

interface AccountV2Summary {
  attributes?: AccountAttributes;
}

interface GlobalAccountV2SummaryState {
  globalAccountV2Summary: AccountV2Summary;
}

interface RootState {
  globalPolicyDecisionRequestState: GlobalPolicyDecisionRequestState;
  globalAccountV2SummaryState: GlobalAccountV2SummaryState;
}

export const LicensesRowMenu = ({
  toggleDrawer,
  license,
}: LicensesRowMenuProps) => {
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { dispatch } = useContext(LicensesContext);
  const {
    featureState: { purchaseLicenseFlow },
  } = useContext(FeatureContext);
  const purchaseLicenseFlowGrantStatus = useSelector(
    (state: RootState) =>
      state.globalPolicyDecisionRequestState.purchaseLicenseFlowGrantStatus,
  );
  const accountV2Summary = useSelector(
    (state: RootState) =>
      state.globalAccountV2SummaryState.globalAccountV2Summary,
  );
  const inAppPurchaseEnabled =
    accountV2Summary?.attributes?.inAppPurchaseEnabled === 'true' ||
    accountV2Summary?.attributes?.inAppPurchaseEnabled === true;
  const freemiumFlowEnabled =
    accountV2Summary?.attributes?.freemium === 'true' ||
    accountV2Summary?.attributes?.freemium === true;
  const isPurchaseLicenseFlowEnabled =
    purchaseLicenseFlow &&
    inAppPurchaseEnabled &&
    purchaseLicenseFlowGrantStatus &&
    !freemiumFlowEnabled;

  const onEditAssignmentClick = (): void => {
    dispatch({
      type: actionTypes.SET_EDIT_LICENSE_ASSIGNMENT,
      license,
    });

    dispatch({
      type: actionTypes.SET_LICENSE_PARAM,
      licenseParam: '',
    });
    toggleDrawer();
  };
  const onPurchaseLicensesClick = (): void => {
    dispatch({
      type: actionTypes.TOGGLE_PURCHASE_LICENSE_MODAL,
      license,
    });
  };

  return (
    <>
      <div
        className="licenses-row-menu-icon"
        style={{ visibility: menuOpen ? 'visible' : 'hidden' }}
      >
        <StyledMenuIconButton
          onClick={() => {
            setMenuOpen(true);
          }}
          data-testid="PlatformAdminUI-LicensesRow-MenuIconButton"
          aria-label="Menu Icon Button"
          ref={menuButtonRef}
        >
          <StyledMenuIcon icon={Icons.menu} />
        </StyledMenuIconButton>
      </div>
      <Menu
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
        }}
        anchorEl={menuButtonRef.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableAutoFocusItem
      >
        <MenuItem
          onClick={onEditAssignmentClick}
          data-testid="PlatformAdminUI-LicensesRow-EditAssignmentMenuItem"
        >
          <StyledMenuItemContainer>
            <StyledMenuIcon icon={Icons.penLight} />
            <StyledMenuItemText variant="body2">
              Edit assignment
            </StyledMenuItemText>
          </StyledMenuItemContainer>
        </MenuItem>
        {isPurchaseLicenseFlowEnabled && (
          <MenuItem
            onClick={onPurchaseLicensesClick}
            data-testid="PlatformAdminUI-LicensesRow-PurchaseLicensesMenuItem"
          >
            <StyledMenuItemContainer>
              <StyledShoppingCartIcon icon={Icons.shoppingCart} />
              <StyledMenuItemText variant="body2">
                Purchase licenses
              </StyledMenuItemText>
            </StyledMenuItemContainer>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
